import { AnforderungenKarte } from './AnforderungenKarte';
import React, { useCallback, useEffect, useState } from 'react';
import { SchnellstartStand } from './SchnellstartStand';
import { RechtstexteStand } from './RechtstexteStand';
import { Loader2, ShieldCheck, History, UserCheck, Link2 } from 'lucide-react';
import { VerbundKarte } from './VerbundKarte';
import { api } from '../../context/AuthContext';
import { MandatsendeKarte, ZeitKarte } from './MandatKarten';

const dt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; } };
const Karte = ({ titel, icon: Icon, children, testId }) => (
  <section className="bg-white border border-gray-200 rounded-2xl" data-testid={testId}>
    <header className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100"><span className="w-7 h-7 rounded-lg bg-[#b8893d]/10 flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-[#b8893d]" /></span><h3 className="font-serif text-lg text-[#1B3A5C]">{titel}</h3></header>
    <div className="p-5 text-sm text-[#34536b]">{children}</div>
  </section>
);

const StatusKarte = ({ sb, mnr, onSaved }) => {
  const [grund, setGrund] = useState('');
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const aktiv = sb.status !== 'inaktiv';
  const setzen = async () => {
    if (!window.confirm(aktiv ? 'Mandant wirklich auf inaktiv setzen? Portalzugang und Benachrichtigungen werden gesperrt, neue Vorgänge sind nicht mehr möglich. Die Daten bleiben erhalten.' : 'Mandant wieder aktivieren?')) return;
    setBusy(true); setErr('');
    try { onSaved((await api.post(`/register/mandanten/${mnr}/status`, { aktiv: !aktiv, grund, notiz })).data); setGrund(''); setNotiz(''); }
    catch (e) { setErr(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <Karte titel="Status" icon={ShieldCheck} testId="stammblatt-status">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${aktiv ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`} data-testid="stammblatt-status-badge">{aktiv ? 'AKTIV' : 'INAKTIV'}</span>
        {!aktiv && <span className="text-xs">{sb.status_grund_label}{sb.status_notiz ? ` – ${sb.status_notiz}` : ''} · seit {dt(sb.status_am)}</span>}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        {aktiv && (
          <>
            <select value={grund} onChange={(e) => setGrund(e.target.value)} className="h-9 border border-gray-300 rounded-[10px] px-2 text-sm" data-testid="stammblatt-grund">
              <option value="">Grund wählen …</option>
              {Object.entries(sb.gruende).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder={grund === 'sonstiges' ? 'Grund beschreiben (Pflicht)' : 'Notiz (optional)'} className="h-9 border border-gray-300 rounded-[10px] px-2 text-sm flex-1 min-w-[180px]" data-testid="stammblatt-notiz" />
          </>
        )}
        <button type="button" onClick={setzen} disabled={busy || (aktiv && (!grund || (grund === 'sonstiges' && !notiz.trim())))} data-testid="stammblatt-status-umschalten"
          className={`h-9 px-4 rounded-[10px] text-xs text-white disabled:opacity-50 ${aktiv ? 'bg-rose-700 hover:bg-rose-800' : 'bg-[#1B3A5C] hover:bg-[#15304d]'}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : aktiv ? 'Auf inaktiv setzen' : 'Wieder aktivieren'}
        </button>
      </div>
      {err && <p className="mt-2 text-xs text-rose-700" data-testid="stammblatt-fehler">{err}</p>}
    </Karte>
  );
};

const ErfassungsBogen = ({ zeilen }) => {
  const [offen, setOffen] = React.useState(false);
  return (
    <div className="mb-3" data-testid="stammblatt-erfassungsbogen">
      <button type="button" onClick={() => setOffen(!offen)} className="text-xs text-[#b8893d] hover:underline" aria-expanded={offen} data-testid="stammblatt-erfassungsbogen-toggle">{offen ? 'Erfassungsbogen ausblenden' : `Gesamten Erfassungsbogen anzeigen (${zeilen.length} Angaben)`}</button>
      {offen && <dl className="mt-2 border border-gray-100 rounded-xl divide-y divide-gray-50 text-sm" data-testid="stammblatt-erfassungsbogen-inhalt">
        {zeilen.map((z, i) => <div key={`${z.feld}-${i}`} className="grid sm:grid-cols-[14rem_1fr] gap-x-3 px-3 py-1.5"><dt className="text-[#7a8a99] break-words">{z.feld}</dt><dd className="text-[#1B3A5C] break-words whitespace-pre-wrap">{/^\d{4}-\d{2}-\d{2}(T|$)/.test(z.wert) ? new Date(z.wert.length === 10 ? `${z.wert}T12:00:00` : z.wert).toLocaleString('de-DE', z.wert.length === 10 ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' }) : z.wert}</dd></div>)}
      </dl>}
    </div>
  );
};

const ZugangKarte = ({ sb }) => (
  <Karte titel="Registrierung und Zugang" icon={UserCheck} testId="stammblatt-zugang">
    <p className="text-sm mb-3">Herkunft: <b>{sb.herkunft}</b> · angelegt {dt(sb.angelegt_am)}{sb.erfassung ? ` · Widerrufsbelehrung ${sb.erfassung.widerrufsbelehrung ? 'bestätigt' : '—'}, Verlangen nach § 356 Abs. 4 BGB ${sb.erfassung.verlangt_beginn ? 'erklärt' : '—'} am ${dt(sb.erfassung.am)}` : ''}</p>
    {sb.mandanten_nr && <a href={`/kanzlei/benutzer?tab=mandantenkonten&mnr=${encodeURIComponent(sb.mandanten_nr)}`} className="inline-block mb-3 text-xs text-[#b8893d] hover:underline" data-testid="stammblatt-rechte-verwalten">Rechte verwalten</a>}
    {sb.erfassung?.bogen?.length > 0 && <ErfassungsBogen zeilen={sb.erfassung.bogen} />}
    {!sb.konten.length ? <p className="text-xs text-gray-500" data-testid="stammblatt-keine-konten">Kein Portalkonto verknüpft.</p> : (
      <ul className="space-y-3">
        {sb.konten.map((k) => (
          <li key={k.email} className="border border-gray-100 bg-[#f5f8fa]/60 rounded-xl p-4 text-xs leading-relaxed" data-testid="stammblatt-konto">
            <div className="text-base text-[#1B3A5C] font-medium">{k.name || k.email} <span className="text-[10px] text-[#b8893d] ml-1">{k.rolle}</span>{k.gesperrt && <span className="ml-2 text-rose-700">gesperrt</span>}</div>
            <div className="mt-1 grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
              <span>E-Mail: {k.email} {k.email_bestaetigt ? '(bestätigt)' : '(unbestätigt)'}</span>
              <span>Registriert: {dt(k.registriert_am)} · verknüpft {dt(k.verknuepft_am)}{k.via ? ` (${k.via})` : ''}</span>
              <span>Anmeldeweg: {k.anmeldeweg}</span>
              <span>Letzte Anmeldung: {dt(k.letzte_anmeldung)}</span>
              <span>Zwei-Faktor: {k.zwei_fa ? 'aktiv' : 'nicht eingerichtet'}</span>
              <RechtstexteStand rt={k.rechtstexte} testId={`stammblatt-rechtstexte-${k.email}`} pdf={k.user_id && sb.mandanten_nr ? `/register/mandanten/${sb.mandanten_nr}/zustimmungsnachweis/${k.user_id}.pdf` : null} name={k.email} />
            </div>
            <SchnellstartStand k={k} />
          </li>
        ))}
      </ul>
    )}
  </Karte>
);

const VerlaufKarte = ({ verlauf }) => (
  <Karte titel="Verlauf" icon={History} testId="stammblatt-verlauf">
    {!verlauf.length ? <p className="text-xs text-gray-500">Noch keine Änderungen.</p> : (
      <ul className="divide-y divide-gray-100 text-sm max-h-80 overflow-y-auto">
        {verlauf.map((v, i) => (
          <li key={i} className="py-2 flex flex-wrap gap-x-3" data-testid="stammblatt-verlauf-eintrag">
            <span className="text-gray-400 w-28 shrink-0">{dt(v.am)}</span><span className="font-medium text-[#1B3A5C]">{v.feld}</span>
            <span className="break-all">{v.alt ?? '—'} → {v.neu ?? '—'}</span><span className="text-gray-400 ml-auto">{v.von}</span>
          </li>
        ))}
      </ul>
    )}
  </Karte>
);

export const Stammblatt = ({ mnr, tick, onVerbund }) => {
  const [sb, setSb] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get(`/register/mandanten/${mnr}/stammblatt`).then((r) => setSb(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Laden fehlgeschlagen')), [mnr]);
  useEffect(() => { load(); }, [load, tick]);
  if (err) return <p className="text-sm text-rose-700">{err}</p>;
  if (!sb) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" /></div>;
  return (
    <div className="space-y-4" data-testid="stammblatt">
      <Karte titel="Verknüpfte Mandantennummern" icon={Link2} testId="stammblatt-verbund"><VerbundKarte mnr={mnr} onChanged={onVerbund} /></Karte>
      <AnforderungenKarte mnr={mnr} tick={tick} />
      <StatusKarte sb={sb} mnr={mnr} onSaved={setSb} />
      <ZugangKarte sb={sb} />
      <ZeitKarte mnr={mnr} />
      <MandatsendeKarte sb={sb} mnr={mnr} onDone={load} />
      <VerlaufKarte verlauf={sb.verlauf} />
    </div>
  );
};
